import React, { useEffect, useState } from "react";
import {
    CircularProgress,
    Dialog,
    IconButton,
    TextField,
} from "@mui/material";
import { Shield } from "lucide-react";
import { useFormik } from "formik";
import * as yup from "yup";
import {
    createWhitelistIpAction,
    editWhiteListIpAction,
} from "../../Actions/whiteListpsAction";
import { MyCloseIcon } from "../../Assets/svg/MyIcons";
import { FRONTEND_MESSAGES } from "../../utils/messages";
import "../../components/Common/FormModal.css";

const { VALIDATION } = FRONTEND_MESSAGES;

const AddEditWhiteListIp = ({
    open,
    handleClose,
    ipDataById,
    setSnackbar,
    getWhiteListIps,
}) => {
    const [buttonDisable, setButtonDisable] = useState(false);
    const [loading, setLoading] = useState(false);
    const isEdit = Boolean(ipDataById?.data?.id);

    const validationSchema = yup.object().shape({
        ipAdress: yup
            .string()
            .trim()
            .required(VALIDATION.REQUIRED_FIELD("IP address"))
            .min(7, VALIDATION.MIN_LENGTH("IP address", 7))
            .max(15, VALIDATION.MAX_LENGTH("IP address", 15))
            .matches(
                /^((25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/,
                VALIDATION.INVALID_FORMAT("IP address")
            ),
    });

    const formik = useFormik({
        initialValues: {
            ipAdress: ipDataById?.data.ipAddress
                ? ipDataById.data.ipAddress
                : "",
        },
        validationSchema: validationSchema,
        enableReinitialize: true,
        onSubmit: async (values) => {
            setButtonDisable(true);
            setLoading(true);
            const ipAdress = values.ipAdress.trim();
            const data = {
                ipAddres: ipAdress,
            };

            if (ipDataById) {
                await editWhiteListIpAction(ipDataById?.data.id, data)
                    .then(() => {
                        setSnackbar({
                            open: true,
                            message: "IP has been updated successfully",
                            severity: "success",
                        });
                        formik.resetForm();
                        handleClose();
                        getWhiteListIps();
                    })
                    .catch((error) => {
                        setSnackbar({
                            open: true,
                            message: error.message,
                            severity: "error",
                        });
                    })
                    .finally(() => {
                        setLoading(false);
                        setButtonDisable(false);
                    });
            } else {
                await createWhitelistIpAction(data)
                    .then(() => {
                        setSnackbar({
                            open: true,
                            message: "IP added successfully",
                            severity: "success",
                        });
                        formik.resetForm();
                        handleClose();
                        getWhiteListIps();
                    })
                    .catch((error) => {
                        setSnackbar({
                            open: true,
                            message:
                                error?.response?.data?.message ||
                                JSON.parse(error?.response?.data)?.message,
                            severity: "error",
                        });
                    })
                    .finally(() => {
                        setLoading(false);
                        setButtonDisable(false);
                    });
            }
        },
    });

    useEffect(() => {
        if (ipDataById) {
            formik.setFieldValue("ipAdress", ipDataById?.data.ipAddress);
        }
        // eslint-disable-next-line
    }, [ipDataById]);

    const handleIpChange = (e) => {
        formik.setFieldValue(e.target.name, e.target.value.replace(/\s/g, ""));
        formik.setFieldTouched(e.target.name, true, false);
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="sm"
            PaperProps={{ className: "fm-dialog" }}
        >
            <div className="fm-header">
                <span className="fm-header-icon" aria-hidden>
                    <Shield size={20} />
                </span>
                <div className="fm-header-copy">
                    <h2>{isEdit ? "Update IP Address" : "Add IP Address"}</h2>
                    <p>
                        {isEdit
                            ? "Update the allowed IP for Master Portal access."
                            : "Whitelist an IP address that can access Master Portal."}
                    </p>
                </div>
                <IconButton
                    aria-label="close"
                    className="fm-close"
                    onClick={handleClose}
                >
                    <MyCloseIcon />
                </IconButton>
            </div>

            <form onSubmit={formik.handleSubmit} className="fm-form">
                <div className="fm-body">
                    <section className="fm-section">
                        <div className="fm-section-head">
                            <h3>Network access</h3>
                            <p>
                                Enter a valid IPv4 address (for example
                                192.168.1.10).
                            </p>
                        </div>
                        <div className="fm-card">
                            <div className="fm-grid is-stack">
                                <div className="fm-field">
                                    <label htmlFor="whitelist-ip">
                                        IP address
                                        <span className="fm-req">*</span>
                                    </label>
                                    <TextField
                                        id="whitelist-ip"
                                        size="small"
                                        placeholder="e.g. 192.168.1.10"
                                        name="ipAdress"
                                        fullWidth
                                        value={formik.values.ipAdress}
                                        onChange={handleIpChange}
                                        onBlur={formik.handleBlur}
                                        error={
                                            formik.touched.ipAdress &&
                                            Boolean(formik.errors.ipAdress)
                                        }
                                        helperText={
                                            formik.touched.ipAdress &&
                                            formik.errors.ipAdress
                                        }
                                    />
                                </div>
                            </div>
                        </div>
                    </section>
                </div>

                <div className="fm-footer">
                    <button
                        type="button"
                        className="fm-btn"
                        onClick={handleClose}
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="fm-btn is-primary"
                        disabled={buttonDisable || loading || !formik.isValid}
                    >
                        {isEdit ? "Update" : "Add"}
                        {loading && (
                            <CircularProgress size={16} color="inherit" />
                        )}
                    </button>
                </div>
            </form>
        </Dialog>
    );
};

export default AddEditWhiteListIp;
